import { toast } from "react-toastify";
import {
  getDatabase,
  ref,
  get,
  set,
  push,
  update,
  remove,
} from "firebase/database";
import { GoogleGenerativeAI, SchemaType } from "@google/generative-ai";
import { searchProjectInfo } from "../../services/perplexityService";
import { canAddPostingHours } from "./autoPostLimitUtils";

/**
 * Lấy ví dụ chủ đề theo lĩnh vực hoạt động
 * @param {string} domainId - ID của lĩnh vực hoạt động
 * @returns {string} - Chuỗi ví dụ chủ đề theo lĩnh vực
 */
const getDomainSpecificExamples = (domainId) => {
  const examples = {
    "real-estate": `
Ví dụ chủ đề tốt cho lĩnh vực Bất động sản:
- "5 lý do vì sao căn hộ view sông luôn được săn đón nhất"
- "Bí quyết chọn tầng 'vàng' khi mua chung cư không phải ai cũng biết"
- "Xu hướng thiết kế nội thất 2024: Tối giản nhưng sang trọng"
- "Phong thủy căn hộ: 3 điều cấm kỵ khi bố trí phòng khách"
- "Đầu tư BĐS 2024: Shophouse hay chung cư cho thuê?"`,

    restaurant: `
Ví dụ chủ đề tốt cho lĩnh vực Nhà hàng:
- "Bí quyết pha chế cocktail 'thần thánh' khiến khách hàng mê mẩn"
- "5 món ăn trending nhất mùa hè 2024 không thể bỏ qua"
- "Cách bảo quản thực phẩm tươi ngon cả tuần cho nhà hàng"
- "Menu healthy: Xu hướng ăn uống mới của giới trẻ"
- "Không gian nhà hàng: Làm sao để khách 'check-in' nhiều nhất?"`,

    ecommerce: `
Ví dụ chủ đề tốt cho lĩnh vực Thương mại điện tử:
- "5 sản phẩm hot nhất tuần này khiến dân mạng 'săn lùng'"
- "Mẹo mua sắm online tiết kiệm 50% chi phí không phải ai cũng biết"
- "Unboxing sản phẩm viral TikTok: Có thật sự đáng tiền?"
- "Flash sale cuối tháng: Những deal 'không thể bỏ qua'"
- "Review chi tiết: So sánh 3 thương hiệu nổi tiếng nhất"`,

    healthcare: `
Ví dụ chủ đề tốt cho lĩnh vực Y tế - Sức khỏe:
- "5 dấu hiệu cảnh báo cơ thể thiếu vitamin D nghiêm trọng"
- "Bí quyết giữ dáng sau Tết không cần ăn kiêng khắc nghiệt"
- "Tập thể dục tại nhà: 10 phút mỗi ngày cho sức khỏe tốt"
- "Thực phẩm 'vàng' giúp tăng cường miễn dịch mùa đông"
- "Stress công sở: 7 cách giải tỏa hiệu quả ngay tại bàn làm"`,

    education: `
Ví dụ chủ đề tốt cho lĩnh vực Giáo dục:
- "5 phương pháp học tiếng Anh hiệu quả nhất cho người bận rộn"
- "Kỹ năng mềm nào quan trọng nhất trong thời đại 4.0?"
- "Bí quyết ghi nhớ kiến thức lâu dài không phải ai cũng biết"
- "Xu hướng giáo dục 2024: Học online hay offline hiệu quả hơn?"
- "Cách nuôi dạy con thông minh và tự tin từ nhỏ"`,

    finance: `
Ví dụ chủ đề tốt cho lĩnh vực Tài chính:
- "5 sai lầm đầu tư phổ biến khiến bạn mất tiền oan"
- "Cách tiết kiệm 1 triệu/tháng với mức lương 10 triệu"
- "Đầu tư vàng hay chứng khoán: Lựa chọn nào an toàn hơn?"
- "Bí quyết quản lý tài chính cá nhân hiệu quả cho người trẻ"
- "Xu hướng fintech 2024: Cơ hội hay thách thức?"`,

    professional: `
Ví dụ chủ đề tốt cho lĩnh vực Dịch vụ chuyên nghiệp:
- "5 kỹ năng 'mềm' giúp bạn thăng tiến nhanh chóng trong công việc"
- "Bí quyết xây dựng thương hiệu cá nhân trên LinkedIn"
- "Cách đàm phán lương hiệu quả mà không làm mất lòng sếp"
- "Xu hướng làm việc từ xa: Cơ hội và thách thức"
- "Networking thông minh: Kết nối đúng người, đúng thời điểm"`,

    insurance: `
Ví dụ chủ đề tốt cho lĩnh vực Bảo hiểm:
- "5 loại bảo hiểm cần thiết nhất cho gia đình trẻ"
- "Bảo hiểm y tế: Những điều quan trọng cần biết trước khi mua"
- "Bí quyết chọn gói bảo hiểm phù hợp với thu nhập"
- "Quy trình bồi thường bảo hiểm: Làm sao để nhanh chóng nhất?"
- "Bảo hiểm xe: So sánh các gói để chọn tốt nhất"`,
  };

  return examples[domainId] || "";
};

// Note: checkUserPlan function removed - system is now free to use

/**
 * Lấy số lượng bài viết website còn lại
 * @param {string} userId - ID của người dùng
 * @returns {Promise<number>} - Số lượng bài viết website còn lại
 */
export const fetchWebsitePostQuantity = async (userId) => {
  try {
    if (!userId) return 0;

    const db = getDatabase();
    const websitePostQuantityRef = ref(
      db,
      `users/${userId}/websitePostQuantity`
    );
    const snapshot = await get(websitePostQuantityRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      return 0;
    }
  } catch (error) {
    console.error("Error fetching website post quantity:", error);
    return 0;
  }
};

/**
 * Lưu cài đặt người dùng
 * @param {string} userId - ID của người dùng
 * @param {Object} settings - Cài đặt cần lưu
 * @returns {Promise<boolean>} - Kết quả lưu cài đặt
 */
export const saveUserSettings = async (userId, settings) => {
  try {
    if (!userId) return false;

    const db = getDatabase();
    const settingsRef = ref(db, `users/${userId}/settings/autoPostGenerator`);

    await set(settingsRef, settings);
    return true;
  } catch (error) {
    console.error("Error saving settings:", error);
    return false;
  }
};

/**
 * Tải cài đặt người dùng
 * @param {string} userId - ID của người dùng
 * @returns {Promise<Object|null>} - Cài đặt người dùng
 */
export const loadUserSettings = async (userId) => {
  try {
    if (!userId) return null;

    const db = getDatabase();
    const settingsRef = ref(db, `users/${userId}/settings/autoPostGenerator`);
    const snapshot = await get(settingsRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error("Error loading settings:", error);
    return null;
  }
};

/**
 * Tạo chủ đề Facebook
 * @param {string} requirements - Yêu cầu về nội dung
 * @param {number} topicCount - Số lượng chủ đề cần tạo
 * @param {string} apiKey - API key của Gemini
 * @param {string} referenceUrl - URL tham khảo (tùy chọn)
 * @param {Array} contextFiles - Tài liệu tham khảo (tùy chọn)
 * @param {Object} domainConfig - Cấu hình lĩnh vực hoạt động (tùy chọn)
 * @returns {Promise<Object>} - Kết quả tạo chủ đề
 */
export const generateFacebookTopics = async (
  requirements,
  topicCount,
  apiKey,
  referenceUrl = "",
  contextFiles = [],
  domainConfig = null
) => {
  try {
    // Khởi tạo Google Generative AI client
    const genAI = new GoogleGenerativeAI(apiKey);

    // Tìm kiếm thông tin cập nhật từ Perplexity nếu có requirements
    let perplexityContext = "";
    if (requirements && requirements.trim() !== "") {
      try {
        console.log("🔍 Đang tìm kiếm thông tin cập nhật từ Perplexity...");

        const perplexityResult = await searchProjectInfo(
          requirements,
          domainConfig
        );

        if (perplexityResult.success) {
          perplexityContext = `
THÔNG TIN CẬP NHẬT TỪ PERPLEXITY:
${perplexityResult.content}

Hãy sử dụng thông tin cập nhật này để tạo các chủ đề chính xác và thời sự.`;
          console.log("✅ Đã nhận thông tin cập nhật từ Perplexity");
        } else {
          console.log(
            "⚠️ Không thể lấy thông tin từ Perplexity, sử dụng thông tin gốc"
          );
        }
      } catch (perplexityError) {
        console.warn("⚠️ Lỗi khi gọi Perplexity API:", perplexityError.message);
        // Tiếp tục với thông tin gốc nếu Perplexity không khả dụng
      }
    }

    // Tạo prompt cơ bản với thông tin lĩnh vực hoạt động
    let domainContext = "";
    if (domainConfig && domainConfig.id !== "other") {
      domainContext = `
LĨNH VỰC HOẠT ĐỘNG: ${domainConfig.name} (${domainConfig.description})

Hãy tạo các chủ đề phù hợp với lĩnh vực ${domainConfig.name.toLowerCase()}, sử dụng thuật ngữ và phong cách phù hợp với ngành này.`;

      console.log(
        `🎯 generateFacebookTopics: Sử dụng domain context cho ${domainConfig.name}`
      );
    } else {
      console.log(
        `🎯 generateFacebookTopics: Không sử dụng domain context (domain: ${
          domainConfig?.id || "null"
        })`
      );
    }

    let promptRequest = `
Bạn là một chuyên gia về content marketing trên mạng xã hội Facebook. Nhiệm vụ của bạn là tạo ra ${topicCount} chủ đề bài viết hay, hấp dẫn, thu hút người xem với các thông tin sau: ${requirements}.
${domainContext}
${perplexityContext}

${
  referenceUrl && referenceUrl.trim() !== ""
    ? `
Hãy tham khảo thông tin từ URL: ${referenceUrl} để tạo ra các chủ đề phù hợp và có liên quan đến nội dung của trang web này.
`
    : ""
}

${
  contextFiles && contextFiles.length > 0
    ? `
Hãy tham khảo thông tin từ các tài liệu sau để tạo ra các chủ đề phù hợp:
${contextFiles
  .map((file, index) => {
    if (file.type === "text" || file.type === "pdf") {
      return `
Tài liệu ${index + 1} (${file.name}):
${file.content}
`;
    } else if (file.type === "image") {
      return `
Hình ảnh ${index + 1} (${
        file.name
      }): Hãy phân tích nội dung hình ảnh này để tạo chủ đề phù hợp.
`;
    }
    return "";
  })
  .join("\n")}
`
    : ""
}

Yêu cầu:
- **Chủ đề phải cụ thể, không chứa các placeholder dạng [danh từ], [tên sản phẩm], [địa điểm], v.v. Thay vào đó, hãy sử dụng tên cụ thể nếu có thể hoặc tạo ra các chủ đề chung nhưng vẫn hấp dẫn.**
- Nếu không có thông tin về thời gian, hãy tạo chủ đề phù hợp với năm hiện tại ${new Date().getFullYear()}, đảm bảo tính thời sự và theo xu hướng hiện tại. Chỉ đưa thông tin thời gian vào chủ đề khi thực sự cần thiết và phù hợp với nội dung.
- Tạo chủ đề có tính độc đáo, gây tò mò và thu hút người đọc ngay từ tiêu đề.
- Chủ đề được dùng để viết bài đăng trên fanpage Facebook, vì vậy phải đảm bảo chủ đề ngắn gọn, phù hợp với định dạng và phong cách của Facebook, khuyến khích tương tác (like, comment, share).
- Thay vào đó, hãy sử dụng tên cụ thể của sản phẩm, địa điểm, thương hiệu thực tế và phổ biến nếu có trong yêu cầu. Nếu không, tạo chủ đề chung nhưng vẫn cụ thể và hấp dẫn.
- Ví dụ thay vì "Top 5 tính năng ẩn của [Tên Xe]", hãy viết "Khám phá 5 tính năng ẩn ít người biết của Toyota Camry 2024" hoặc "5 mẹo sử dụng ô tô hiệu quả không phải ai cũng biết".
- Là các chủ đề, tin tức mới, đang được quan tâm và lan tỏa trên mạng xã hội.
- Không có chủ đề nào bị trùng lặp.
- Tránh các chủ đề chung chung, mơ hồ hoặc mang tính lý thuyết quá sâu.
- **Tuyệt đối không đưa các thông tin cá nhân, thông tin liên hệ vào chủ đề.**

Ví dụ chủ đề tốt cho Facebook:
- "5 quán Mì Quảng 'gây nghiện' ở Đà Nẵng dân địa phương hay ghé"
- "Bí quyết pha nước chấm bánh tráng cuốn thịt heo Đà Nẵng 'thần thánh'"
- "Food tour Đà Nẵng 1 ngày: Ăn sập các món ngon từ sáng đến tối"
- "Giải nhiệt mùa hè Đà Nẵng với 5 món chè 'ngon quên lối về'"
- "Top quán cafe view đẹp 'sống ảo' cực chất ở Đà Nẵng"

${
  domainConfig && domainConfig.id !== "other"
    ? getDomainSpecificExamples(domainConfig.id)
    : ""
}
`;

    // Sử dụng thư viện Google Generative AI
    const model = genAI.getGenerativeModel({
      model: "gemini-2.0-flash", // Sử dụng model phù hợp
    });

    const generationConfig = {
      responseMimeType: "application/json",
      responseSchema: {
        description: "List of Facebook post topics",
        type: SchemaType.ARRAY,
        items: {
          type: SchemaType.STRING,
        },
      },
    };

    // Nếu có URL tham khảo, validate và thêm vào prompt
    if (referenceUrl && referenceUrl.trim() !== "") {
      try {
        // Validate URL format
        new URL(referenceUrl);
        console.log("Sử dụng URL context của Gemini:", referenceUrl);

        // Thêm URL trực tiếp vào prompt để Gemini tự động truy cập và phân tích
        promptRequest += `\n\nVui lòng truy cập và phân tích nội dung từ URL sau để tạo chủ đề phù hợp: ${referenceUrl}`;
      } catch (urlError) {
        console.warn("URL không hợp lệ:", referenceUrl);
        return {
          success: false,
          error:
            "URL không hợp lệ. Vui lòng kiểm tra định dạng URL (ví dụ: https://example.com).",
        };
      }
    }

    // Chuẩn bị parts cho request
    const parts = [{ text: promptRequest }];

    console.log("🚀 generateFacebookTopics: Final prompt request:");
    console.log(promptRequest);

    // Thêm hình ảnh từ context files nếu có
    if (contextFiles && contextFiles.length > 0) {
      contextFiles.forEach((file) => {
        if (file.type === "image" && file.content) {
          // Chuyển đổi base64 data URL thành format mà Gemini hiểu
          const base64Data = file.content.split(",")[1];
          const mimeType = file.content.split(";")[0].split(":")[1];

          parts.push({
            inlineData: {
              data: base64Data,
              mimeType: mimeType,
            },
          });
        }
      });
    }

    const result = await model.generateContent({
      contents: [{ parts: parts }],
      generationConfig: generationConfig,
    });

    const response = result.response;
    let jsonData = response.text();

    // Xử lý dữ liệu JSON
    const rawData = JSON.parse(jsonData);
    // Lọc bỏ các chủ đề chứa dấu ngoặc vuông không mong muốn
    const filteredData = rawData.filter((topic) => !/\[.*?\]/.test(topic));

    const topicsDict = filteredData.reduce((acc, topic, index) => {
      acc[`topic-${index + 1}`] = {
        id: `topic-${index + 1}`,
        title: topic,
        status: "pending",
        postStatus: "not_started",
      };
      return acc;
    }, {});

    return { success: true, topics: topicsDict };
  } catch (error) {
    console.error("Failed to generate Facebook topics:", error);

    // Xử lý lỗi cụ thể cho URL context
    if (
      error.message &&
      (error.message.includes("URL") || error.message.includes("url"))
    ) {
      return {
        success: false,
        error:
          "URL tham khảo không hợp lệ hoặc không thể truy cập. Vui lòng kiểm tra lại URL hoặc thử URL khác.",
      };
    }

    return { success: false, error: error.message };
  }
};

/**
 * Xóa một chủ đề
 * @param {Object} topics - Danh sách chủ đề hiện tại
 * @param {string} topicId - ID của chủ đề cần xóa
 * @returns {Object} - Danh sách chủ đề sau khi xóa
 */
export const deleteTopic = (topics, topicId) => {
  const newTopics = { ...topics };
  delete newTopics[topicId];
  return newTopics;
};

/**
 * Reset trạng thái các chủ đề
 * @param {Object} topics - Danh sách chủ đề hiện tại
 * @returns {Object} - Danh sách chủ đề sau khi reset
 */
export const resetTopicsStatus = (topics) => {
  const updatedTopics = {};
  Object.keys(topics).forEach((key) => {
    updatedTopics[key] = {
      ...topics[key],
      status: "pending",
      postStatus: "not_started",
    };
  });
  return updatedTopics;
};

/**
 * Auto-save generated post for Auto Post Generator
 * Backend should call this after generating content but before publishing
 * @param {string} userId - User ID
 * @param {Object} configId - Auto-post config ID
 * @param {Object} generatedContent - Generated post content
 * @param {Array} generatedImages - Generated post images
 * @returns {Promise<Object>} - Result of auto-save
 * 
 * NOTE: Backend implementation should:
 * 1. Call this function in autoPostService after Gemini generates content
 * 2. Save to: users/{userId}/generated_posts/{postId}
 * 3. Structure: similar to Normal Post Generator but with source: "auto"
 * 4. Include autoPostConfigId reference for tracking
 */
export const autoSaveAutoPostContent = async (
  userId,
  configId,
  generatedContent,
  generatedImages
) => {
  // This function signature is for frontend reference
  // Actual implementation should be in backend autoPostService
  console.log(
    "📝 Auto Post content generated - should be auto-saved by backend:",
    { userId, configId, contentLength: generatedContent?.length }
  );
};

/**
 * Lưu cài đặt đăng bài tự động
 * @param {Object} config - Cấu hình hiện tại
 * @param {Object} settingsToSave - Cài đặt cần lưu
 * @param {string} userId - ID của người dùng
 * @returns {Promise<Object>} - Kết quả lưu cài đặt
 */
export const saveAutoPostSettings = async (config, settingsToSave, userId) => {
  try {
    // Kiểm tra giới hạn nếu autoPostEnabled = true
    if (settingsToSave.autoPostEnabled) {
      const times = settingsToSave.times || [];
      const checkResult = await canAddPostingHours(
        userId,
        config.id,
        times,
        true
      );

      if (!checkResult.canAdd) {
        return {
          success: false,
          error: `Vượt quá giới hạn ${checkResult.maxHours} giờ đăng bài. Hiện tại: ${checkResult.totalHours} giờ, bạn muốn thêm ${times.length} giờ (tổng: ${checkResult.totalAfterAdd} giờ).`,
          limitExceeded: true,
        };
      }
    }

    const db = getDatabase();
    const settingsRef = config.id
      ? ref(db, `posts/${userId}/${config.id}`)
      : ref(db, `posts/${userId}`);

    if (config.id) {
      await update(settingsRef, settingsToSave);
      return { success: true, id: config.id };
    } else {
      const newRef = await push(settingsRef, settingsToSave);
      return { success: true, id: newRef.key };
    }
  } catch (error) {
    console.error("Failed to save settings:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Xóa cấu hình
 * @param {Object} config - Cấu hình cần xóa
 * @param {string} userId - ID của người dùng
 * @returns {Promise<Object>} - Kết quả xóa cấu hình
 */
export const deleteConfig = async (config, userId) => {
  try {
    if (!config.id) {
      return { success: false, error: "Không có thiết lập để xóa!" };
    }

    const db = getDatabase();
    const configRef = ref(db, `posts/${userId}/${config.id}`);
    await remove(configRef);
    return { success: true };
  } catch (error) {
    console.error("Failed to delete config:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Tải danh sách fanpage từ Firebase
 * @param {string} userId - ID của người dùng
 * @returns {Promise<Object>} - Kết quả tải danh sách fanpage
 */
export const loadFacebookData = async (userId) => {
  try {
    if (!userId) return { success: false, error: "Không có userId" };

    const db = getDatabase();
    const connectedFanpagesRef = ref(db, `users/${userId}/connectedFanpages`);
    const connectedFanpagesSnapshot = await get(connectedFanpagesRef);

    if (connectedFanpagesSnapshot.exists()) {
      const connectedData = connectedFanpagesSnapshot.val();
      const fanpageList = Object.values(connectedData);

      return { success: true, fanpages: fanpageList };
    } else {
      return {
        success: false,
        error: "Không tìm thấy fanpage nào trong Firebase",
      };
    }
  } catch (error) {
    console.error("Error fetching fanpages from Firebase:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Tải danh sách WordPress sites từ Firebase
 * @param {string} userId - ID của người dùng
 * @param {Object} config - Cấu hình hiện tại
 * @returns {Promise<Object>} - Kết quả tải danh sách WordPress sites
 */
export const fetchWordpressSites = async (userId, config) => {
  try {
    if (!userId) return { success: false, error: "Không có userId" };

    const db = getDatabase();
    const wordpressSitesRef = ref(db, `users/${userId}/wordpressSites`);
    const wordpressSitesSnapshot = await get(wordpressSitesRef);

    if (wordpressSitesSnapshot.exists()) {
      const sitesData = wordpressSitesSnapshot.val();
      const sitesArray = Object.keys(sitesData).map((key) => ({
        id: key,
        ...sitesData[key],
      }));

      let siteSelectedFromConfig = null;

      if (config && config.id) {
        // Trường hợp 1: Config mới với wordpressSiteId
        if (config.wordpressSiteId) {
          const selectedSite = sitesArray.find(
            (site) => site.id === config.wordpressSiteId
          );
          if (selectedSite) {
            siteSelectedFromConfig = selectedSite;
          }
        }
        // Trường hợp 2: Config cũ có wordpressUrl, username, password
        else if (config.wordpressUrl && config.wordpressUsername) {
          // Kiểm tra xem URL đã tồn tại trong danh sách site chưa
          let matchedSite = sitesArray.find(
            (site) =>
              site.url.includes(config.wordpressUrl) ||
              config.wordpressUrl.includes(site.url)
          );

          if (matchedSite) {
            siteSelectedFromConfig = matchedSite;
          }
        }
      }

      return {
        success: true,
        sites: sitesArray,
        selectedSite: siteSelectedFromConfig,
      };
    } else {
      return { success: true, sites: [] };
    }
  } catch (error) {
    console.error("Error fetching WordPress sites:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Viết hoa chữ cái đầu tiên của chuỗi
 * @param {string} string - Chuỗi cần viết hoa
 * @returns {string} - Chuỗi sau khi viết hoa
 */
export const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};
